import { ORTHO_DELTAS, cloneBoard, cloneCell, parsePosKey, posKey } from "./grid";
import { makeRandomTile, makeRefillTile } from "./board";
import {
  DOOM_CLOCK_BOMB3_BONUS_SEC,
  DOOM_CLOCK_BOMB4_BONUS_SEC,
  DOOM_CLOCK_BOMB5_BONUS_SEC,
  DOOM_CLOCK_WIPE_MATCHED_TILE_BONUS_SEC,
  DOOM_CLOCK_WIPE_USE_BONUS_SEC,
  GROOVE_BOMB_TILE_BONUS_FRAC,
  GROOVE_WIPE_MATCHED_TILE_BONUS_FRAC
} from "./constants";
import { emptyObjectiveTelemetry } from "./progression";
import { emptySoloStageTelemetry, mergeSoloStageTelemetry } from "./soloModes";
import { findMatchGroups, hasAnyMatch } from "./match";
import { grooveBonusFracForMatch, scoreClearEvent, scorePassiveMatchEvent } from "./scoring";
import { pickSpawnCell, specialFromMatch } from "./specials";
import { Rng } from "./rng";
import type { BombSeed, SpecialSwapActivation, WipeSwapPayload } from "./specials";
import type {
  BeatState,
  BoardState,
  CollapseAnimStep,
  DetonatedBombType,
  GrooveState,
  LevelConfig,
  MatchVisualGroup,
  MatchTier,
  ObjectiveTelemetry,
  Position,
  ResolveResult,
  SpecialKind,
  SoloStageTelemetry,
  TileData,
  TileMotion
} from "./types";

export interface ResolveContext {
  board: BoardState;
  level: LevelConfig;
  rng: Rng;
  groove: GrooveState;
  beat: BeatState;
  nowSec: number;
  preferredSpawn?: Position;
  refillEnabled?: boolean;
  refillTargetColors?: number[];
}

const CRATE_REVEAL_SPECIALS: Array<"BOMB_3" | "BOMB_4" | "BOMB_5" | "WIPE"> = [
  "BOMB_3",
  "BOMB_4",
  "BOMB_5",
  "WIPE"
];

const OBJECTIVE_TELEMETRY_KEYS: Array<keyof ObjectiveTelemetry> = [
  "line4PlusMatches",
  "line5PlusMatches",
  "shapeMatches",
  "specialsCreated",
  "bombTriggers",
  "wipeTriggers",
  "specialTriggers",
  "crateBreaks",
  "obstacleClears",
  "specialClearTiles",
  "wipeClearTiles",
  "chainDepth2PlusResolves",
  "chainDepth3PlusResolves",
  "chainDepth4PlusResolves"
];

function addObjectiveTelemetry(target: ObjectiveTelemetry, delta: Partial<ObjectiveTelemetry>): void {
  for (const key of OBJECTIVE_TELEMETRY_KEYS) {
    target[key] += delta[key] ?? 0;
  }
}

function mergeObjectiveTelemetry(
  base: ObjectiveTelemetry | undefined,
  next: ObjectiveTelemetry | undefined
): ObjectiveTelemetry {
  const out = emptyObjectiveTelemetry();
  addObjectiveTelemetry(out, base ?? emptyObjectiveTelemetry());
  addObjectiveTelemetry(out, next ?? emptyObjectiveTelemetry());
  return out;
}

function cloneTileData(tile: TileData): TileData {
  return {
    color: tile.color,
    special: tile.special,
    lockHp: tile.lockHp
  };
}

function stepWithSnapshot(board: BoardState, step: CollapseAnimStep): CollapseAnimStep {
  return {
    ...step,
    boardSnapshot: cloneBoard(board)
  };
}

function pushVisualStep(steps: CollapseAnimStep[], board: BoardState, step: CollapseAnimStep): void {
  steps.push(stepWithSnapshot(board, step));
}

function overlayTileAt(board: BoardState, row: number, col: number): { row: number; col: number; tile: TileData } | null {
  const cell = board.cells[row]?.[col];
  if (cell?.kind !== "TILE" || cell.tile == null) {
    return null;
  }
  return {
    row,
    col,
    tile: cloneTileData(cell.tile)
  };
}

function clearCell(board: BoardState, row: number, col: number): boolean {
  const cell = board.cells[row][col];
  if (cell.kind === "EMPTY") {
    return false;
  }
  board.cells[row][col] = { kind: "EMPTY", tile: null };
  return true;
}

function unlockAdjacent(board: BoardState, row: number, col: number): number {
  let unlocked = 0;
  for (const delta of ORTHO_DELTAS) {
    const nr = row + delta.row;
    const nc = col + delta.col;
    if (nr < 0 || nc < 0 || nr >= board.size || nc >= board.size) {
      continue;
    }
    const neighbor = board.cells[nr][nc];
    if (neighbor.kind === "TILE" && neighbor.tile != null && neighbor.tile.lockHp > 0) {
      neighbor.tile.lockHp = 0;
      unlocked += 1;
    }
  }
  return unlocked;
}

function clearAdjacentCrates(board: BoardState, row: number, col: number, clearSet: Set<string>): void {
  for (const delta of ORTHO_DELTAS) {
    const nr = row + delta.row;
    const nc = col + delta.col;
    if (nr < 0 || nc < 0 || nr >= board.size || nc >= board.size) {
      continue;
    }
    const neighbor = board.cells[nr][nc];
    if (neighbor.kind === "CRATE") {
      clearSet.add(posKey(nr, nc));
    }
  }
}

function recordMotion(
  motions: TileMotion[] | undefined,
  fromRow: number,
  fromCol: number,
  toRow: number,
  toCol: number,
  spawn: boolean,
  tile: TileData
): void {
  if (motions == null) {
    return;
  }
  motions.push({
    fromRow,
    fromCol,
    toRow,
    toCol,
    spawn,
    tile: cloneTileData(tile)
  });
}

function bombSizeFromSpecial(special: SpecialKind): number {
  if (special === "BOMB_3") return 3;
  if (special === "BOMB_4") return 4;
  if (special === "BOMB_5") return 5;
  return 0;
}

function isBombSpecial(special: SpecialKind): special is "BOMB_3" | "BOMB_4" | "BOMB_5" {
  return special === "BOMB_3" || special === "BOMB_4" || special === "BOMB_5";
}

function gravityVector(board: BoardState): Position {
  if (board.gravity === "DOWN") return { row: 1, col: 0 };
  if (board.gravity === "UP") return { row: -1, col: 0 };
  if (board.gravity === "LEFT") return { row: 0, col: -1 };
  return { row: 0, col: 1 };
}

function inBounds(board: BoardState, row: number, col: number): boolean {
  return row >= 0 && row < board.size && col >= 0 && col < board.size;
}

interface SteelStageResult {
  motions: Array<{
    fromRow: number;
    fromCol: number;
    toRow: number;
    toCol: number;
    fellThroughEdge?: boolean;
  }>;
  crushCells: Position[];
  crushedTileCount: number;
  steelRemovedCount: number;
  soloStageTelemetry: SoloStageTelemetry;
}

function emptySteelStageResult(): SteelStageResult {
  return {
    motions: [],
    crushCells: [],
    crushedTileCount: 0,
    steelRemovedCount: 0,
    soloStageTelemetry: emptySoloStageTelemetry()
  };
}

function hasSteelCells(board: BoardState): boolean {
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      if (board.cells[row][col].kind === "STEEL") {
        return true;
      }
    }
  }
  return false;
}

function orderedSteelCells(board: BoardState): Position[] {
  const steels: Position[] = [];
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      if (board.cells[row][col].kind === "STEEL") {
        steels.push({ row, col });
      }
    }
  }
  steels.sort((a, b) => {
    if (board.gravity === "DOWN") {
      if (a.row !== b.row) return b.row - a.row;
      return a.col - b.col;
    }
    if (board.gravity === "UP") {
      if (a.row !== b.row) return a.row - b.row;
      return a.col - b.col;
    }
    if (board.gravity === "RIGHT") {
      if (a.col !== b.col) return b.col - a.col;
      return a.row - b.row;
    }
    if (a.col !== b.col) return a.col - b.col;
    return a.row - b.row;
  });
  return steels;
}

function applySteelWeightedFall(board: BoardState): SteelStageResult {
  const result = emptySteelStageResult();
  const dir = gravityVector(board);
  const steels = orderedSteelCells(board);

  for (const steel of steels) {
    const current = board.cells[steel.row]?.[steel.col];
    if (current?.kind !== "STEEL") {
      continue;
    }

    board.cells[steel.row][steel.col] = { kind: "EMPTY", tile: null };
    let landingRow = steel.row;
    let landingCol = steel.col;
    let moved = false;
    let fellThroughEdge = false;

    while (true) {
      const nextRow = landingRow + dir.row;
      const nextCol = landingCol + dir.col;
      if (!inBounds(board, nextRow, nextCol)) {
        if (dir.row === 1 && dir.col === 0) {
          fellThroughEdge = true;
        }
        break;
      }
      if (board.cells[nextRow][nextCol].kind === "EMPTY") {
        landingRow = nextRow;
        landingCol = nextCol;
        moved = true;
        continue;
      }
      break;
    }

    if (fellThroughEdge) {
      result.steelRemovedCount += 1;
      result.motions.push({
        fromRow: steel.row,
        fromCol: steel.col,
        toRow: landingRow + dir.row,
        toCol: landingCol + dir.col,
        fellThroughEdge: true
      });
      continue;
    }

    board.cells[landingRow][landingCol] = { kind: "STEEL", tile: null };
    if (moved) {
      result.motions.push({
        fromRow: steel.row,
        fromCol: steel.col,
        toRow: landingRow,
        toCol: landingCol
      });
    }
  }

  return result;
}

interface PostClearSettleOptions {
  visualSteps: CollapseAnimStep[];
  steelChainIndex: number;
  finalStepTier: MatchTier;
  finalClearedCells: Position[];
  finalCrateBreakCells: Position[];
  finalMatchedGroups: MatchVisualGroup[];
  resolveFinalChainIndex: (steelStage: SteelStageResult) => number;
  specialSpawns?: Array<{ row: number; col: number; kind: SpecialKind }>;
}

function pushPostClearFollowupStep(
  steps: CollapseAnimStep[],
  board: BoardState,
  tier: MatchTier,
  motions: TileMotion[],
  clearedCells: Position[],
  crateBreakCells: Position[],
  matchedGroups: MatchVisualGroup[],
  chainIndex: number,
  specialSpawns?: Array<{ row: number; col: number; kind: SpecialKind }>
): void {
  if (
    motions.length === 0
    && clearedCells.length === 0
    && crateBreakCells.length === 0
    && matchedGroups.length === 0
  ) {
    return;
  }
  pushVisualStep(steps, board, {
    tier,
    motions,
    clearedCells,
    crateBreakCells,
    matchedGroups,
    chainIndex,
    ...(specialSpawns != null && specialSpawns.length > 0 ? { specialSpawns } : {})
  });
}

function applyPostClearSettle(
  ctx: ResolveContext,
  options: PostClearSettleOptions
): SteelStageResult {
  const refillEnabled = ctx.refillEnabled !== false;
  const preSteelMotions: TileMotion[] = [];
  applyGravity(ctx.board, preSteelMotions);
  const steelStage = hasSteelCells(ctx.board)
    ? applySteelWeightedFall(ctx.board)
    : emptySteelStageResult();
  const finalChainIndex = options.resolveFinalChainIndex(steelStage);

  if (steelStage.motions.length > 0) {
    const crushCells = steelStage.crushCells.map((cell) => ({ row: cell.row, col: cell.col }));
    const steelMatchedGroups = crushCells.length > 0
      ? [
          {
            cells: crushCells.map((cell) => ({ row: cell.row, col: cell.col })),
            shape: "LINE" as const,
            center: centerFromCells(crushCells)
          }
        ]
      : [];
    pushVisualStep(options.visualSteps, ctx.board, {
      tier: 3,
      motions: preSteelMotions,
      clearedCells: crushCells,
      crateBreakCells: [],
      matchedGroups: steelMatchedGroups,
      chainIndex: options.steelChainIndex,
      specialFx: "STEEL_CRUSH",
      steelMotions: steelStage.motions.map((motion) => ({ ...motion })),
      ...(crushCells.length > 0 ? { steelCrushCells: crushCells } : {})
    });

    const followupMotions: TileMotion[] = [];
    applyGravity(ctx.board, followupMotions);
    if (refillEnabled) {
      refillBoard(ctx.board, ctx.level, ctx.rng, followupMotions, ctx.refillTargetColors);
    }
    pushPostClearFollowupStep(
      options.visualSteps,
      ctx.board,
      options.finalStepTier,
      followupMotions,
      options.finalClearedCells,
      options.finalCrateBreakCells,
      options.finalMatchedGroups,
      finalChainIndex,
      options.specialSpawns
    );
    return steelStage;
  }

  if (refillEnabled) {
    refillBoard(ctx.board, ctx.level, ctx.rng, preSteelMotions, ctx.refillTargetColors);
  }
  pushPostClearFollowupStep(
    options.visualSteps,
    ctx.board,
    options.finalStepTier,
    preSteelMotions,
    options.finalClearedCells,
    options.finalCrateBreakCells,
    options.finalMatchedGroups,
    finalChainIndex,
    options.specialSpawns
  );
  return steelStage;
}

interface SteelStageSummary {
  coinEligibleClears: number;
  totalTilesCleared: number;
  groupsCleared: number;
  scoreGained: number;
  earnedKey: boolean;
  steelRemovedCount: number;
  scoreEvent: ResolveResult["scoreEvents"][number] | null;
  soloStageTelemetry: SoloStageTelemetry;
}

function summarizeSteelStage(
  steelStage: SteelStageResult
): SteelStageSummary {
  return {
    coinEligibleClears: steelStage.steelRemovedCount,
    totalTilesCleared: 0,
    groupsCleared: 0,
    scoreGained: 0,
    earnedKey: false,
    steelRemovedCount: steelStage.steelRemovedCount,
    scoreEvent: null,
    soloStageTelemetry: mergeSoloStageTelemetry(undefined, steelStage.soloStageTelemetry)
  };
}

interface BombWaveFrame {
  tick: number;
  cells: Position[];
}

interface BombDetonationCounts {
  BOMB_3: number;
  BOMB_4: number;
  BOMB_5: number;
}

interface BombWaveResult {
  clearCells: Set<string>;
  triggeredBombCount: number;
  detonatedBombsByType: BombDetonationCounts;
  frames: BombWaveFrame[];
}

function buildBombWaveFromSeeds(
  board: BoardState,
  bombSeeds: BombSeed[]
): BombWaveResult {
  const clearCells = new Set<string>();
  const frameCellsByTick = new Map<number, Set<string>>();
  const queue: Array<BombSeed & { startTick: number }> = bombSeeds.map((seed) => ({
    center: { row: seed.center.row, col: seed.center.col },
    special: seed.special,
    startTick: 0
  }));
  const queuedBombs = new Set(queue.map((seed) => posKey(seed.center.row, seed.center.col)));
  const detonatedBombs = new Set<string>();
  const detonatedBombsByType: BombDetonationCounts = {
    BOMB_3: 0,
    BOMB_4: 0,
    BOMB_5: 0
  };

  while (queue.length > 0) {
    queue.sort((a, b) => a.startTick - b.startTick);
    const next = queue.shift();
    if (next == null) {
      break;
    }
    const centerKey = posKey(next.center.row, next.center.col);
    if (detonatedBombs.has(centerKey)) {
      continue;
    }
    detonatedBombs.add(centerKey);
    detonatedBombsByType[next.special] += 1;

    const size = bombSizeFromSpecial(next.special);
    if (size <= 0) {
      continue;
    }
    const half = Math.floor(size / 2);
    const startRow = next.center.row - half;
    const startCol = next.center.col - half;

    for (let radius = 0; radius <= half; radius += 1) {
      const tick = next.startTick + radius;
      let frameSet = frameCellsByTick.get(tick);
      if (frameSet == null) {
        frameSet = new Set<string>();
        frameCellsByTick.set(tick, frameSet);
      }

      for (let row = startRow; row < startRow + size; row += 1) {
        for (let col = startCol; col < startCol + size; col += 1) {
          if (!inBounds(board, row, col)) {
            continue;
          }
          const distance = Math.max(Math.abs(row - next.center.row), Math.abs(col - next.center.col));
          if (distance !== radius) {
            continue;
          }
          const cell = board.cells[row][col];
          if (cell.kind === "STEEL") {
            continue;
          }
          const key = posKey(row, col);
          frameSet.add(key);

          const wipeProtected =
            cell.kind === "TILE" && cell.tile != null && cell.tile.special === "WIPE";
          if (!wipeProtected) {
            clearCells.add(key);
          }

          if (cell.kind !== "TILE" || cell.tile == null || !isBombSpecial(cell.tile.special)) {
            continue;
          }
          if (detonatedBombs.has(key) || queuedBombs.has(key)) {
            continue;
          }
          queue.push({
            center: { row, col },
            special: cell.tile.special,
            startTick: tick + 1
          });
          queuedBombs.add(key);
        }
      }
    }
  }

  const frames = [...frameCellsByTick.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([tick, cells]) => ({
      tick,
      cells: [...cells].map(parsePosKey)
    }));

  return {
    clearCells,
    triggeredBombCount: detonatedBombs.size,
    detonatedBombsByType,
    frames
  };
}

function makeBombWaveVisualSteps(
  preClearSnapshot: BoardState,
  postClearBoard: BoardState,
  frames: BombWaveFrame[],
  clearCells: Set<string>,
  origin: Position,
  tier: MatchTier,
  crateBreakCells: Position[] = []
): CollapseAnimStep[] {
  const firstClearTickByCell = new Map<string, number>();
  const crateBreakCellSet = new Set(crateBreakCells.map((cell) => posKey(cell.row, cell.col)));
  for (const frame of frames) {
    for (const cell of frame.cells) {
      const key = posKey(cell.row, cell.col);
      if (!clearCells.has(key)) {
        continue;
      }
      const existing = firstClearTickByCell.get(key);
      if (existing == null || frame.tick < existing) {
        firstClearTickByCell.set(key, frame.tick);
      }
    }
  }

  return frames.map((frame) => {
    const frameSnapshot = cloneBoard(preClearSnapshot);
    for (const [key, tick] of firstClearTickByCell.entries()) {
      if (tick > frame.tick) {
        continue;
      }
      const { row, col } = parsePosKey(key);
      if (crateBreakCellSet.has(key)) {
        frameSnapshot.cells[row][col] = cloneCell(postClearBoard.cells[row][col]);
        continue;
      }
      if (tick < frame.tick) {
        frameSnapshot.cells[row][col] = { kind: "EMPTY", tile: null };
      }
    }
    return {
      tier,
      motions: [],
      clearedCells: frame.cells.map((cell) => ({ row: cell.row, col: cell.col })),
      crateBreakCells: frame.cells
        .filter((cell) => crateBreakCellSet.has(posKey(cell.row, cell.col)))
        .map((cell) => ({ row: cell.row, col: cell.col })),
      matchedGroups: frame.cells.length > 0
        ? [
            {
              cells: frame.cells.map((cell) => ({ row: cell.row, col: cell.col })),
              shape: "COMPOSITE",
              center: centerFromCells(frame.cells)
            }
          ]
        : [],
      chainIndex: 0,
      specialFx: "BOMB_EXPLOSION",
      wipeMatchedCells: [],
      durationOverrideSec: 0.08,
      bombWaveTick: frame.tick,
      bombWaveOrigin: { row: origin.row, col: origin.col },
      boardSnapshot: frameSnapshot
    };
  });
}

export function applyGravity(board: BoardState, motions?: TileMotion[]): void {
  const n = board.size;
  const cratesFall = board.crateGravity === true;
  if (board.gravity === "DOWN") {
    for (let col = 0; col < n; col += 1) {
      let target = n - 1;
      for (let row = n - 1; row >= 0; row -= 1) {
        const cell = board.cells[row][col];
        if (cell.kind === "STEEL" || (cell.kind === "CRATE" && !cratesFall)) {
          target = row - 1;
          continue;
        }
        if (cell.kind === "TILE" || (cell.kind === "CRATE" && cratesFall)) {
          if (row !== target) {
            if (cell.tile != null) {
              recordMotion(motions, row, col, target, col, false, cell.tile);
            }
            board.cells[target][col] = cell;
            board.cells[row][col] = { kind: "EMPTY", tile: null };
          }
          target -= 1;
        }
      }
    }
    return;
  }

  if (board.gravity === "UP") {
    for (let col = 0; col < n; col += 1) {
      let target = 0;
      for (let row = 0; row < n; row += 1) {
        const cell = board.cells[row][col];
        if (cell.kind === "STEEL" || (cell.kind === "CRATE" && !cratesFall)) {
          target = row + 1;
          continue;
        }
        if (cell.kind === "TILE" || (cell.kind === "CRATE" && cratesFall)) {
          if (row !== target) {
            if (cell.tile != null) {
              recordMotion(motions, row, col, target, col, false, cell.tile);
            }
            board.cells[target][col] = cell;
            board.cells[row][col] = { kind: "EMPTY", tile: null };
          }
          target += 1;
        }
      }
    }
    return;
  }

  if (board.gravity === "RIGHT") {
    for (let row = 0; row < n; row += 1) {
      let target = n - 1;
      for (let col = n - 1; col >= 0; col -= 1) {
        const cell = board.cells[row][col];
        if (cell.kind === "STEEL" || (cell.kind === "CRATE" && !cratesFall)) {
          target = col - 1;
          continue;
        }
        if (cell.kind === "TILE" || (cell.kind === "CRATE" && cratesFall)) {
          if (col !== target) {
            if (cell.tile != null) {
              recordMotion(motions, row, col, row, target, false, cell.tile);
            }
            board.cells[row][target] = cell;
            board.cells[row][col] = { kind: "EMPTY", tile: null };
          }
          target -= 1;
        }
      }
    }
    return;
  }

  for (let row = 0; row < n; row += 1) {
    let target = 0;
    for (let col = 0; col < n; col += 1) {
      const cell = board.cells[row][col];
      if (cell.kind === "STEEL" || (cell.kind === "CRATE" && !cratesFall)) {
        target = col + 1;
        continue;
      }
      if (cell.kind === "TILE" || (cell.kind === "CRATE" && cratesFall)) {
        if (col !== target) {
          if (cell.tile != null) {
            recordMotion(motions, row, col, row, target, false, cell.tile);
          }
          board.cells[row][target] = cell;
          board.cells[row][col] = { kind: "EMPTY", tile: null };
        }
        target += 1;
      }
    }
  }
}

function spawnFromForCell(board: BoardState, row: number, col: number): Position {
  if (board.gravity === "DOWN") {
    return { row: -1, col };
  }
  if (board.gravity === "UP") {
    return { row: board.size, col };
  }
  if (board.gravity === "LEFT") {
    return { row, col: board.size };
  }
  return { row, col: -1 };
}

export function refillBoard(
  board: BoardState,
  level: LevelConfig,
  rng: Rng,
  motions?: TileMotion[],
  refillTargetColors?: readonly number[]
): void {
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      const cell = board.cells[row][col];
      if (cell.kind === "EMPTY") {
        const tile = makeRefillTile(level, rng, refillTargetColors, 0);
        const spawnFrom = spawnFromForCell(board, row, col);
        recordMotion(motions, spawnFrom.row, spawnFrom.col, row, col, true, tile);
        board.cells[row][col] = {
          kind: "TILE",
          tile
        };
      }
    }
  }
}

function clearByCellKeys(
  board: BoardState,
  clearSet: Set<string>,
  allowSteel: boolean,
  level: LevelConfig,
  rng: Rng
): {
  tilesCleared: number;
  crateBreakCount: number;
  crateBreakCells: Position[];
  steelCleared: number;
  lockClearedCount: number;
  coinEligibleClears: number;
  clearedTileKeys: string[];
  soloStageTelemetry: SoloStageTelemetry;
} {
  let tilesCleared = 0;
  let crateBreakCount = 0;
  const crateBreakCells: Position[] = [];
  let steelCleared = 0;
  let lockClearedCount = 0;
  let coinEligibleClears = 0;
  const clearedTileKeys: string[] = [];
  const soloStageTelemetry = emptySoloStageTelemetry();
  for (const key of clearSet) {
    const { row, col } = parsePosKey(key);
    const cell = board.cells[row]?.[col];
    if (cell == null) continue;

    if (cell.kind === "STEEL" && !allowSteel) {
      continue;
    }
    if (cell.kind === "CRATE") {
      const tile = makeRandomTile(level, rng, 0);
      tile.special = CRATE_REVEAL_SPECIALS[rng.nextInt(CRATE_REVEAL_SPECIALS.length)] ?? "BOMB_3";
      board.cells[row][col] = {
        kind: "TILE",
        tile
      };
      crateBreakCount += 1;
      crateBreakCells.push({ row, col });
      coinEligibleClears += 1;
      continue;
    }
    if (cell.kind === "TILE") {
      if (cell.tile != null && cell.tile.lockHp > 0) {
        lockClearedCount += 1;
      }
      if (cell.tile != null) {
        soloStageTelemetry.clearedRows[row] += 1;
        soloStageTelemetry.clearedColors[String(cell.tile.color)] =
          (soloStageTelemetry.clearedColors[String(cell.tile.color)] ?? 0) + 1;
        soloStageTelemetry.clearedTiles += 1;
        clearedTileKeys.push(posKey(row, col));
      }
      tilesCleared += 1;
      coinEligibleClears += 1;
    } else if (cell.kind === "STEEL") {
      steelCleared += 1;
      coinEligibleClears += 1;
    }
    clearCell(board, row, col);
  }
  return {
    tilesCleared,
    crateBreakCount,
    crateBreakCells,
    steelCleared,
    lockClearedCount,
    coinEligibleClears,
    clearedTileKeys,
    soloStageTelemetry
  };
}

function tierFromRunLength(runLength: number): MatchTier {
  if (runLength >= 5) {
    return 5;
  }
  if (runLength >= 4) {
    return 4;
  }
  return 3;
}

function centerFromCells(cells: Position[]): Position {
  if (cells.length === 0) {
    return { row: 5, col: 5 };
  }
  const row = cells.reduce((acc, cell) => acc + cell.row, 0) / cells.length;
  const col = cells.reduce((acc, cell) => acc + cell.col, 0) / cells.length;
  return { row: Math.round(row), col: Math.round(col) };
}

function posKeysFromCells(cells: Position[]): string[] {
  return cells.map((cell) => posKey(cell.row, cell.col));
}

export function bombChainBonusForDetonations(bombsDetonated: number): number {
  const detonations = Math.max(0, Math.floor(bombsDetonated));
  if (detonations <= 3) {
    return detonations * 150;
  }
  let total = 450;
  let currentIncrement = 150;
  for (let i = 4; i <= detonations; i += 1) {
    currentIncrement *= 1.05;
    total += currentIncrement;
  }
  return total;
}

function bombDoomBonusForDetonationsByType(counts: BombDetonationCounts): number {
  return (
    counts.BOMB_3 * DOOM_CLOCK_BOMB3_BONUS_SEC
    + counts.BOMB_4 * DOOM_CLOCK_BOMB4_BONUS_SEC
    + counts.BOMB_5 * DOOM_CLOCK_BOMB5_BONUS_SEC
  );
}

function bombCashForDetonationsByType(counts: BombDetonationCounts): number {
  return counts.BOMB_3 * 3 + counts.BOMB_4 * 4 + counts.BOMB_5 * 5;
}

function totalBombDetonationsByType(counts: BombDetonationCounts): number {
  return counts.BOMB_3 + counts.BOMB_4 + counts.BOMB_5;
}

function strongerBomb(
  current: DetonatedBombType | null,
  candidate: DetonatedBombType | null
): DetonatedBombType | null {
  if (candidate == null) {
    return current;
  }
  if (current == null) {
    return candidate;
  }
  if (candidate === "BOMB_5" || current === "BOMB_3") {
    return candidate;
  }
  if (candidate === "BOMB_4" && current !== "BOMB_5") {
    return candidate;
  }
  return current;
}

function strongestBombFromCounts(counts: BombDetonationCounts): DetonatedBombType | null {
  if (counts.BOMB_5 > 0) {
    return "BOMB_5";
  }
  if (counts.BOMB_4 > 0) {
    return "BOMB_4";
  }
  if (counts.BOMB_3 > 0) {
    return "BOMB_3";
  }
  return null;
}

function wipeDoomBonusForMatchedTiles(matchedTileCount: number): number {
  const matchedCount = Math.max(0, Math.floor(matchedTileCount));
  return DOOM_CLOCK_WIPE_USE_BONUS_SEC + matchedCount * DOOM_CLOCK_WIPE_MATCHED_TILE_BONUS_SEC;
}

function bombGrooveBonusForTiles(clearedTileCount: number): number {
  const clearedCount = Math.max(0, Math.floor(clearedTileCount));
  return clearedCount * GROOVE_BOMB_TILE_BONUS_FRAC;
}

function wipeGrooveBonusForMatchedTiles(matchedTileCount: number): number {
  const matchedCount = Math.max(0, Math.floor(matchedTileCount));
  return matchedCount * GROOVE_WIPE_MATCHED_TILE_BONUS_FRAC;
}

function bombRawScore(tilesCleared: number, bombsDetonated: number): number {
  return Math.max(0, tilesCleared) * 20 + bombChainBonusForDetonations(bombsDetonated);
}

function wipeRawScore(tilesCleared: number): number {
  return Math.max(0, tilesCleared) * 100;
}

interface ResolvedWipeTargetGroup {
  color: number;
  matchedCells: Position[];
}

function collectVisibleRegularCellsByColor(board: BoardState): Map<number, Position[]> {
  const cellsByColor = new Map<number, Position[]>();
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      const cell = board.cells[row][col];
      if (
        cell.kind !== "TILE"
        || cell.tile == null
        || cell.tile.special !== "NONE"
      ) {
        continue;
      }
      const cells = cellsByColor.get(cell.tile.color);
      if (cells == null) {
        cellsByColor.set(cell.tile.color, [{ row, col }]);
      } else {
        cells.push({ row, col });
      }
    }
  }
  return cellsByColor;
}

function uniqueSortedColors(colors: number[]): number[] {
  return [...new Set(colors)].sort((a, b) => a - b);
}

function resolveWipeTargetGroups(
  board: BoardState,
  wipe: WipeSwapPayload,
  rng: Rng
): ResolvedWipeTargetGroup[] {
  const cellsByColor = collectVisibleRegularCellsByColor(board);
  let colors: number[] = [];
  if (wipe.selection.kind === "FIXED_COLORS") {
    colors = uniqueSortedColors(wipe.selection.colors).filter((color) => cellsByColor.has(color));
  } else {
    colors = [...cellsByColor.keys()].sort((a, b) => a - b);
    rng.shuffleInPlace(colors);
    colors = colors.slice(0, Math.min(wipe.selection.count, colors.length));
  }
  return colors.map((color) => ({
    color,
    matchedCells: (cellsByColor.get(color) ?? []).map((cell) => ({ row: cell.row, col: cell.col }))
  })).filter((group) => group.matchedCells.length > 0);
}

function flattenWipeMatchedCells(groups: ResolvedWipeTargetGroup[]): Position[] {
  return groups.flatMap((group) => group.matchedCells.map((cell) => ({ row: cell.row, col: cell.col })));
}

function buildWipeClearSet(groups: ResolvedWipeTargetGroup[], activators: Position[]): Set<string> {
  const clearSet = new Set<string>();
  for (const group of groups) {
    for (const cell of group.matchedCells) {
      clearSet.add(posKey(cell.row, cell.col));
    }
  }
  for (const activator of activators) {
    clearSet.add(posKey(activator.row, activator.col));
  }
  return clearSet;
}

function powerupSweepRawScore(tilesCleared: number): number {
  return Math.max(0, tilesCleared) * 100;
}

function runCascadeLoop(
  ctx: ResolveContext,
  startChainIndex: number
): ResolveResult {
  let chainIndex = startChainIndex;
  let scoreGained = 0;
  let groupsCleared = 0;
  let totalTilesCleared = 0;
  let coinEligibleClears = 0;
  let earnedKey = false;
  let crateBreakCount = 0;
  const scoreEvents: ResolveResult["scoreEvents"] = [];
  const visualSteps: CollapseAnimStep[] = [];
  const objectiveTelemetry = emptyObjectiveTelemetry();
  const soloStageTelemetry = emptySoloStageTelemetry();
  const reserved = new Map<string, SpecialKind>();

  while (true) {
    const groups = findMatchGroups(ctx.board);
    if (groups.length === 0) {
      break;
    }

    reserved.clear();
    const clearSet = new Set<string>();

    for (const group of groups) {
      const special = specialFromMatch(group, ctx.level.level);
      if (group.shape === "LINE" && group.maxRunLength >= 4) {
        objectiveTelemetry.line4PlusMatches += 1;
      }
      if (group.shape === "LINE" && group.maxRunLength >= 5) {
        objectiveTelemetry.line5PlusMatches += 1;
      }
      if (group.shape === "SQUARE" || group.shape === "L" || group.shape === "T" || group.shape === "COMPOSITE") {
        objectiveTelemetry.shapeMatches += 1;
        if (group.shape === "SQUARE") objectiveTelemetry.squareMatches += 1;
        if (group.shape === "L") objectiveTelemetry.lMatches += 1;
        if (group.shape === "T") objectiveTelemetry.tMatches += 1;
      }
      if (special !== "NONE") {
        objectiveTelemetry.specialsCreated += 1;
        const spawn = pickSpawnCell(group, ctx.preferredSpawn);
        reserved.set(posKey(spawn.row, spawn.col), special);
      }
      for (const cell of group.cells) {
        clearSet.add(posKey(cell.row, cell.col));
      }
    }

    for (const key of reserved.keys()) {
      clearSet.delete(key);
    }

    for (const group of groups) {
      for (const cell of group.cells) {
        objectiveTelemetry.obstacleClears += unlockAdjacent(ctx.board, cell.row, cell.col);
        clearAdjacentCrates(ctx.board, cell.row, cell.col, clearSet);
      }
    }

    const clearedCells = [...clearSet].map(parsePosKey);
    const matchedGroups: MatchVisualGroup[] = groups.map((group) => ({
      cells: group.cells.map((cell) => ({ row: cell.row, col: cell.col })),
      shape: group.shape,
      center: group.center
    }));
    const strongestRun = groups.reduce((maxLen, group) => Math.max(maxLen, group.maxRunLength), 3);

    const cleared = clearByCellKeys(ctx.board, clearSet, false, ctx.level, ctx.rng);
    const clearedTiles = cleared.tilesCleared;
    crateBreakCount += cleared.crateBreakCount;
    objectiveTelemetry.crateBreaks += cleared.crateBreakCount;
    objectiveTelemetry.obstacleClears += cleared.crateBreakCount + cleared.steelCleared + cleared.lockClearedCount;
    const mergedClearSoloTelemetry = mergeSoloStageTelemetry(soloStageTelemetry, cleared.soloStageTelemetry);
    soloStageTelemetry.clearedRows = mergedClearSoloTelemetry.clearedRows;
    soloStageTelemetry.clearedColors = mergedClearSoloTelemetry.clearedColors;
    soloStageTelemetry.clearedTiles = mergedClearSoloTelemetry.clearedTiles;
    totalTilesCleared += clearedTiles;
    coinEligibleClears += cleared.coinEligibleClears;
    groupsCleared += groups.length;

    for (const [key, special] of reserved.entries()) {
      const { row, col } = parsePosKey(key);
      let cell = ctx.board.cells[row][col];
      if (cell.kind !== "TILE" || cell.tile == null) {
        ctx.board.cells[row][col] = {
          kind: "TILE",
          tile: makeRandomTile(ctx.level, ctx.rng, 0)
        };
        cell = ctx.board.cells[row][col];
      }
      if (cell.tile != null) {
        cell.tile.special = special;
        cell.tile.lockHp = 0;
      }
    }

    for (const group of groups) {
      const grooveBonusFrac = grooveBonusFracForMatch(group.shape, group.maxRunLength);
      const scored = scorePassiveMatchEvent(ctx.groove, {
        tilesCleared: group.cells.length,
        matchLen: group.maxRunLength,
        chainIndex,
        shape: group.shape,
        level: ctx.level.level
      });
      scoreGained += scored.points;
      scoreEvents.push({
        tilesCleared: group.cells.length,
        matchLen: group.maxRunLength,
        chainIndex,
        points: scored.points,
        shape: group.shape,
        anchor: group.center,
        scoredCellKeys: posKeysFromCells(group.cells),
        cashEligible: true,
        ...(grooveBonusFrac > 0 ? { grooveBonusFrac } : {})
      });
      if (group.maxRunLength >= 4 || chainIndex >= 2) {
        earnedKey = true;
      }
    }

    const specialSpawns: Array<{ row: number; col: number; kind: SpecialKind }> = [];
    for (const [key, special] of reserved.entries()) {
      const pos = parsePosKey(key);
      specialSpawns.push({ row: pos.row, col: pos.col, kind: special });
    }

    const steelStage = applyPostClearSettle(ctx, {
      visualSteps,
      steelChainIndex: chainIndex,
      finalStepTier: tierFromRunLength(strongestRun),
      finalClearedCells: clearedCells,
      finalCrateBreakCells: cleared.crateBreakCells,
      finalMatchedGroups: matchedGroups,
      resolveFinalChainIndex: () => chainIndex,
      specialSpawns: specialSpawns.length > 0 ? specialSpawns : undefined
    });
    const steelSummary = summarizeSteelStage(steelStage);
    coinEligibleClears += steelSummary.coinEligibleClears;
    totalTilesCleared += steelSummary.totalTilesCleared;
    groupsCleared += steelSummary.groupsCleared;
    scoreGained += steelSummary.scoreGained;
    if (steelSummary.scoreEvent != null) {
      scoreEvents.push(steelSummary.scoreEvent);
    }
    objectiveTelemetry.obstacleClears += steelSummary.steelRemovedCount;
    const mergedSteelSoloTelemetry = mergeSoloStageTelemetry(soloStageTelemetry, steelSummary.soloStageTelemetry);
    soloStageTelemetry.clearedRows = mergedSteelSoloTelemetry.clearedRows;
    soloStageTelemetry.clearedColors = mergedSteelSoloTelemetry.clearedColors;
    soloStageTelemetry.clearedTiles = mergedSteelSoloTelemetry.clearedTiles;
    if (steelSummary.earnedKey) {
      earnedKey = true;
    }

    chainIndex += 1;
  }

  const chainDepth = Math.max(0, chainIndex - startChainIndex);
  if (chainDepth >= 2) {
    objectiveTelemetry.chainDepth2PlusResolves = 1;
  }
  if (chainDepth >= 3) {
    objectiveTelemetry.chainDepth3PlusResolves = 1;
  }
  if (chainDepth >= 4) {
    objectiveTelemetry.chainDepth4PlusResolves = 1;
  }

  return {
    groupsCleared,
    totalTilesCleared,
    coinEligibleClears,
    chainDepth,
    scoreGained,
    earnedKey,
    crateBreakCount,
    scoreEvents,
    visualSteps,
    objectiveTelemetry,
    soloStageTelemetry,
    haptics: {
      strongestBomb: null,
      wipeActivated: false
    }
  };
}

export function resolveCascades(ctx: ResolveContext): ResolveResult {
  return runCascadeLoop(ctx, 0);
}

export function resolvePostRotationSettle(ctx: ResolveContext): ResolveResult {
  const out = emptyResolveResult();
  const steelStage = applyPostClearSettle(ctx, {
    visualSteps: out.visualSteps,
    steelChainIndex: 0,
    finalStepTier: 3,
    finalClearedCells: [],
    finalCrateBreakCells: [],
    finalMatchedGroups: [],
    resolveFinalChainIndex: () => 0
  });
  const steelSummary = summarizeSteelStage(steelStage);
  out.coinEligibleClears += steelSummary.coinEligibleClears;
  out.totalTilesCleared += steelSummary.totalTilesCleared;
  out.groupsCleared += steelSummary.groupsCleared;
  out.scoreGained += steelSummary.scoreGained;
  out.earnedKey = out.earnedKey || steelSummary.earnedKey;
  out.objectiveTelemetry.obstacleClears += steelSummary.steelRemovedCount;
  out.soloStageTelemetry = mergeSoloStageTelemetry(out.soloStageTelemetry, steelSummary.soloStageTelemetry);
  if (steelSummary.scoreEvent != null) {
    out.scoreEvents.push(steelSummary.scoreEvent);
  }
  if (!hasAnyMatch(ctx.board)) {
    return out;
  }
  const cascades = runCascadeLoop(ctx, 0);
  return mergeResolveResults(out, cascades);
}

export function resolveSwapByPriorityStages(
  ctx: ResolveContext,
  activation: SpecialSwapActivation
): ResolveResult {
  if (!activation.triggered) {
    return runCascadeLoop(ctx, 0);
  }

  let groupsCleared = 0;
  let totalTilesCleared = 0;
  let coinEligibleClears = 0;
  let scoreGained = 0;
  let earnedKey = false;
  let crateBreakCount = 0;
  const scoreEvents: ResolveResult["scoreEvents"] = [];
  const visualSteps: CollapseAnimStep[] = [];
  const objectiveTelemetry = emptyObjectiveTelemetry();
  let soloStageTelemetry = emptySoloStageTelemetry();
  let hadSpecialClear = false;
  let strongestBomb: DetonatedBombType | null = null;
  let wipeActivated = false;

  const pushScoreEvent = (
    rawPoints: number,
    tilesCleared: number,
    matchLen: number,
    shape: "LINE" | "COMPOSITE",
    anchor: Position,
    scoredCellKeys: string[],
    doomBonusSec = 0,
    grooveBonusFrac = 0,
    cashAmount?: number
  ): void => {
    scoreGained += rawPoints;
    const scoreEvent: ResolveResult["scoreEvents"][number] = {
      tilesCleared,
      matchLen,
      chainIndex: 0,
      points: rawPoints,
      shape,
      anchor,
      scoredCellKeys
    };
    if (doomBonusSec > 0) {
      scoreEvent.doomBonusSec = doomBonusSec;
    }
    if (grooveBonusFrac > 0) {
      scoreEvent.grooveBonusFrac = grooveBonusFrac;
    }
    if (cashAmount != null) {
      scoreEvent.cashAmount = cashAmount;
    }
    scoreEvents.push(scoreEvent);
    if (tilesCleared >= 4) {
      earnedKey = true;
    }
  };

  if (activation.wipe != null) {
    const wipeTargetGroups = resolveWipeTargetGroups(ctx.board, activation.wipe, ctx.rng);
    const wipeMatchedCells = flattenWipeMatchedCells(wipeTargetGroups);
    const wipeClearSet = buildWipeClearSet(wipeTargetGroups, activation.wipe.activators);
    const wipeClearedCells = [...wipeClearSet].map(parsePosKey);
    const wipeBoardSnapshot = cloneBoard(ctx.board);
    const wipeCleared = clearByCellKeys(ctx.board, wipeClearSet, false, ctx.level, ctx.rng);
    const wipeTilesCleared = wipeCleared.tilesCleared;
    crateBreakCount += wipeCleared.crateBreakCount;
    objectiveTelemetry.crateBreaks += wipeCleared.crateBreakCount;
    objectiveTelemetry.obstacleClears += wipeCleared.crateBreakCount + wipeCleared.steelCleared + wipeCleared.lockClearedCount;
    soloStageTelemetry = mergeSoloStageTelemetry(soloStageTelemetry, wipeCleared.soloStageTelemetry);
    coinEligibleClears += wipeCleared.coinEligibleClears;
    if (wipeTilesCleared > 0) {
      wipeActivated = true;
      objectiveTelemetry.wipeTriggers += 1;
      objectiveTelemetry.specialTriggers += 1;
      objectiveTelemetry.wipeClearTiles += wipeTilesCleared;
      objectiveTelemetry.specialClearTiles += wipeTilesCleared;
      const wipeCenter = centerFromCells(wipeClearedCells);
      const wipeMatchLen = Math.max(3, Math.min(10, wipeTilesCleared));
      const wipeDoomBonusSec = wipeDoomBonusForMatchedTiles(wipeMatchedCells.length);
      const wipeGrooveBonusFrac = wipeGrooveBonusForMatchedTiles(wipeMatchedCells.length);
      pushScoreEvent(
        wipeRawScore(wipeTilesCleared),
        wipeTilesCleared,
        wipeMatchLen,
        "COMPOSITE",
        wipeCenter,
        wipeCleared.clearedTileKeys,
        wipeDoomBonusSec,
        wipeGrooveBonusFrac,
        wipeTilesCleared * 5
      );
      groupsCleared += 1;
      totalTilesCleared += wipeTilesCleared;
      hadSpecialClear = true;
      visualSteps.push(stepWithSnapshot(wipeBoardSnapshot, {
        tier: tierFromRunLength(wipeMatchLen),
        motions: [],
        clearedCells: wipeClearedCells,
        crateBreakCells: wipeCleared.crateBreakCells,
        matchedGroups: [
          {
            cells: wipeClearedCells.map((cell) => ({ row: cell.row, col: cell.col })),
            shape: "COMPOSITE",
            center: wipeCenter
          }
        ],
        chainIndex: 0,
        specialFx: "WIPE_DISSOLVE",
        wipeColor: wipeTargetGroups[0]?.color,
        wipeMatchedCells,
        wipeTargetGroups: wipeTargetGroups.map((group) => ({
          color: group.color,
          cells: group.matchedCells.map((cell) => ({ row: cell.row, col: cell.col }))
        })),
        wipeActivatorCells: activation.wipe.activators.map((cell) => ({ row: cell.row, col: cell.col })),
        durationOverrideSec: activation.wipe.activators.length > 1 ? 1.7 : 1.5
      }));
    }
  }

  if (activation.bombSeeds.length > 0) {
    const bombStage = buildBombWaveFromSeeds(ctx.board, activation.bombSeeds);
    strongestBomb = strongerBomb(strongestBomb, strongestBombFromCounts(bombStage.detonatedBombsByType));
    const bombOrigin = activation.bombSeeds[0]?.center ?? { row: 5, col: 5 };
    const bombClearedCells = [...bombStage.clearCells].map(parsePosKey);
    const bombPreClearSnapshot = cloneBoard(ctx.board);
    const bombCleared = clearByCellKeys(ctx.board, bombStage.clearCells, false, ctx.level, ctx.rng);
    const bombTilesCleared = bombCleared.tilesCleared;
    crateBreakCount += bombCleared.crateBreakCount;
    objectiveTelemetry.bombTriggers += bombStage.triggeredBombCount;
    objectiveTelemetry.specialTriggers += bombStage.triggeredBombCount;
    objectiveTelemetry.crateBreaks += bombCleared.crateBreakCount;
    objectiveTelemetry.obstacleClears += bombCleared.crateBreakCount + bombCleared.steelCleared + bombCleared.lockClearedCount;
    soloStageTelemetry = mergeSoloStageTelemetry(soloStageTelemetry, bombCleared.soloStageTelemetry);
    coinEligibleClears += bombCleared.coinEligibleClears;
    const bombMatchLen = Math.max(3, Math.min(10, Math.max(1, bombTilesCleared)));
    const bombTier = tierFromRunLength(bombMatchLen);
    visualSteps.push(
      ...makeBombWaveVisualSteps(
        bombPreClearSnapshot,
        ctx.board,
        bombStage.frames,
        bombStage.clearCells,
        bombOrigin,
        bombTier,
        bombCleared.crateBreakCells
      )
    );
    if (bombTilesCleared > 0) {
      objectiveTelemetry.specialClearTiles += bombTilesCleared;
      const bombCenter = centerFromCells(bombClearedCells);
      const bombDoomBonusSec = bombDoomBonusForDetonationsByType(bombStage.detonatedBombsByType);
      pushScoreEvent(
        bombRawScore(bombTilesCleared, bombStage.triggeredBombCount),
        bombTilesCleared,
        bombMatchLen,
        "COMPOSITE",
        bombCenter,
        bombCleared.clearedTileKeys,
        bombDoomBonusSec,
        bombGrooveBonusForTiles(bombTilesCleared),
        bombCashForDetonationsByType(bombStage.detonatedBombsByType)
      );
      groupsCleared += 1;
      totalTilesCleared += bombTilesCleared;
      hadSpecialClear = true;
    }
  }

  const steelStage = applyPostClearSettle(ctx, {
    visualSteps,
    steelChainIndex: 0,
    finalStepTier: 3,
    finalClearedCells: [],
    finalCrateBreakCells: [],
    finalMatchedGroups: [],
    resolveFinalChainIndex: () => (hadSpecialClear ? 1 : 0)
  });
  const steelSummary = summarizeSteelStage(steelStage);
  coinEligibleClears += steelSummary.coinEligibleClears;
  totalTilesCleared += steelSummary.totalTilesCleared;
  groupsCleared += steelSummary.groupsCleared;
  scoreGained += steelSummary.scoreGained;
  if (steelSummary.scoreEvent != null) {
    scoreEvents.push(steelSummary.scoreEvent);
  }
  objectiveTelemetry.obstacleClears += steelSummary.steelRemovedCount;
  soloStageTelemetry = mergeSoloStageTelemetry(soloStageTelemetry, steelSummary.soloStageTelemetry);
  if (steelSummary.earnedKey) {
    earnedKey = true;
    hadSpecialClear = true;
  }
  if (steelSummary.groupsCleared > 0) {
    hadSpecialClear = true;
  }

  const cascades = runCascadeLoop(ctx, hadSpecialClear ? 1 : 0);

  return {
    groupsCleared: groupsCleared + cascades.groupsCleared,
    totalTilesCleared: totalTilesCleared + cascades.totalTilesCleared,
    coinEligibleClears: coinEligibleClears + cascades.coinEligibleClears,
    chainDepth: (hadSpecialClear ? 1 : 0) + cascades.chainDepth,
    scoreGained: scoreGained + cascades.scoreGained,
    earnedKey: earnedKey || cascades.earnedKey,
    crateBreakCount: crateBreakCount + cascades.crateBreakCount,
    scoreEvents: [...scoreEvents, ...cascades.scoreEvents],
    visualSteps: [...visualSteps, ...cascades.visualSteps],
    objectiveTelemetry: mergeObjectiveTelemetry(objectiveTelemetry, cascades.objectiveTelemetry),
    soloStageTelemetry: mergeSoloStageTelemetry(soloStageTelemetry, cascades.soloStageTelemetry),
    haptics: {
      strongestBomb: strongerBomb(strongestBomb, cascades.haptics.strongestBomb),
      wipeActivated: wipeActivated || cascades.haptics.wipeActivated
    }
  };
}

export function resolveBombDetonationInPlace(
  ctx: ResolveContext,
  center: Position
): ResolveResult {
  if (!inBounds(ctx.board, center.row, center.col)) {
    return emptyResolveResult();
  }
  const cell = ctx.board.cells[center.row]?.[center.col];
  if (cell?.kind !== "TILE" || cell.tile == null || !isBombSpecial(cell.tile.special)) {
    return emptyResolveResult();
  }

  const out = emptyResolveResult();
  appendBombResolveStage(ctx, out, [
    {
      center: { row: center.row, col: center.col },
      special: cell.tile.special
    }
  ]);
  out.chainDepth = out.groupsCleared > 0 ? 1 : 0;
  return applyPostPowerupCascade(ctx, out, out.groupsCleared > 0 ? 1 : 0);
}

export function resolveWipeDetonationInPlace(
  ctx: ResolveContext,
  center: Position
): ResolveResult {
  if (!inBounds(ctx.board, center.row, center.col)) {
    return emptyResolveResult();
  }
  const cell = ctx.board.cells[center.row]?.[center.col];
  if (cell?.kind !== "TILE" || cell.tile == null || cell.tile.special !== "WIPE") {
    return emptyResolveResult();
  }

  const wipe: WipeSwapPayload = {
    activators: [{ row: center.row, col: center.col }],
    selection: {
      kind: "RANDOM_VISIBLE_COLORS",
      count: 1
    }
  };
  const out = emptyResolveResult();
  appendWipeResolveStage(ctx, out, wipe);
  out.chainDepth = out.groupsCleared > 0 ? 1 : 0;
  return applyPostPowerupCascade(ctx, out, out.groupsCleared > 0 ? 1 : 0);
}

export function applySpecialSwapClear(
  ctx: ResolveContext,
  clearCells: Set<string>,
  options?: {
    wipeColor?: number | null;
    wipeMatchedCells?: Position[];
    wipeTargetGroups?: Array<{ color: number; cells: Position[] }>;
    wipeActivatorCells?: Position[];
    source?: "BOMB" | "WIPE";
    bombsDetonated?: number;
    bombDetonationsByType?: BombDetonationCounts;
  }
): ResolveResult {
  const allowSteel = options?.source !== "BOMB";
  const clearResult = clearByCellKeys(ctx.board, clearCells, allowSteel, ctx.level, ctx.rng);
  const tilesCleared = clearResult.tilesCleared;
  const totalClearCount = clearResult.tilesCleared + clearResult.crateBreakCount + clearResult.steelCleared;
  const matchLen = Math.max(3, Math.min(10, tilesCleared));
  const strongestBomb = options?.source === "BOMB" && options.bombDetonationsByType != null
    ? strongestBombFromCounts(options.bombDetonationsByType)
    : null;
  const wipeActivated = options?.source === "WIPE" && tilesCleared > 0;
  const objectiveTelemetry = emptyObjectiveTelemetry();
  let soloStageTelemetry = emptySoloStageTelemetry();
  const scoreEvents: ResolveResult["scoreEvents"] = [];
  let scoreGained = 0;
  let earnedKey = false;
  const visualSteps: CollapseAnimStep[] = [];
  const clearedCellList = [...clearCells].map(parsePosKey);
  const clearCenter = centerFromCells(clearedCellList);
  const wipeMatchedCells = options?.wipeMatchedCells?.map((cellPos) => ({ row: cellPos.row, col: cellPos.col })) ?? [];
  const wipeTargetGroups = options?.wipeTargetGroups?.map((group) => ({
    color: group.color,
    cells: group.cells.map((cellPos) => ({ row: cellPos.row, col: cellPos.col }))
  })) ?? [];
  const wipeActivatorCells = options?.wipeActivatorCells?.map((cellPos) => ({ row: cellPos.row, col: cellPos.col })) ?? [];
  objectiveTelemetry.crateBreaks += clearResult.crateBreakCount;
  objectiveTelemetry.obstacleClears += clearResult.crateBreakCount + clearResult.steelCleared + clearResult.lockClearedCount;
  soloStageTelemetry = mergeSoloStageTelemetry(soloStageTelemetry, clearResult.soloStageTelemetry);

  if (tilesCleared > 0) {
    const wipeMatchedCount = wipeMatchedCells.length;
    const bombBonus = bombChainBonusForDetonations(options?.bombsDetonated ?? 0);
    const bombDetonationCount = options?.bombsDetonated
      ?? (options?.bombDetonationsByType != null ? totalBombDetonationsByType(options.bombDetonationsByType) : 0);
    const wipeDoomBonusSec = options?.source === "WIPE"
      ? wipeDoomBonusForMatchedTiles(wipeMatchedCount)
      : 0;
    const bombDoomBonusSec = options?.source === "BOMB" && options?.bombDetonationsByType != null
      ? bombDoomBonusForDetonationsByType(options.bombDetonationsByType)
      : 0;
    const wipeGrooveBonusFrac = options?.source === "WIPE"
      ? wipeGrooveBonusForMatchedTiles(wipeMatchedCount)
      : 0;
    const bombGrooveBonusFrac = options?.source === "BOMB"
      ? bombGrooveBonusForTiles(tilesCleared)
      : 0;
    const cashAmount = options?.source === "WIPE"
      ? tilesCleared * 5
      : options?.source === "BOMB" && options?.bombDetonationsByType != null
        ? bombCashForDetonationsByType(options.bombDetonationsByType)
        : undefined;
    const grooveBonusFrac = wipeGrooveBonusFrac + bombGrooveBonusFrac;
    const doomBonusSec = wipeDoomBonusSec + bombDoomBonusSec;
    if (options?.source === "WIPE") {
      objectiveTelemetry.wipeTriggers += 1;
      objectiveTelemetry.specialTriggers += 1;
      objectiveTelemetry.wipeClearTiles += tilesCleared;
      objectiveTelemetry.specialClearTiles += tilesCleared;
    } else if (options?.source === "BOMB") {
      objectiveTelemetry.bombTriggers += bombDetonationCount;
      objectiveTelemetry.specialTriggers += bombDetonationCount;
      objectiveTelemetry.specialClearTiles += tilesCleared;
    }
    const rawPoints = options?.source === "WIPE"
      ? wipeRawScore(tilesCleared)
      : options?.source === "BOMB"
        ? bombRawScore(tilesCleared, bombDetonationCount)
        : scoreClearEvent(ctx.groove, {
            tilesCleared,
            matchLen,
            chainIndex: 0,
            shape: "COMPOSITE",
            bonusPoints: wipeMatchedCount * 100 + bombBonus,
            level: ctx.level.level
          }).points;
    scoreGained += rawPoints;
    scoreEvents.push({
      tilesCleared,
      matchLen,
      chainIndex: 0,
      points: rawPoints,
      shape: "COMPOSITE",
      anchor: clearCenter,
      scoredCellKeys: clearResult.clearedTileKeys,
      ...(cashAmount != null ? { cashAmount } : {}),
      ...(doomBonusSec > 0 ? { doomBonusSec } : {}),
      ...(grooveBonusFrac > 0 ? { grooveBonusFrac } : {})
    });
    if (tilesCleared >= 4) {
      earnedKey = true;
    }
  }

  if (totalClearCount > 0) {
    pushVisualStep(visualSteps, ctx.board, {
      tier: tierFromRunLength(matchLen),
      motions: [],
      clearedCells: clearedCellList,
      crateBreakCells: clearResult.crateBreakCells,
      matchedGroups: [
        {
          cells: clearedCellList.map((cell) => ({ row: cell.row, col: cell.col })),
          shape: "COMPOSITE",
          center: clearCenter
        }
      ],
      chainIndex: 0,
      specialFx:
        options?.source === "BOMB"
          ? "BOMB_EXPLOSION"
          : options?.source === "WIPE"
            ? "WIPE_DISSOLVE"
            : undefined,
      wipeColor: options?.wipeColor ?? undefined,
      wipeMatchedCells,
      wipeTargetGroups,
      wipeActivatorCells
    });
  }

  const steelStage = applyPostClearSettle(ctx, {
    visualSteps,
    steelChainIndex: 0,
    finalStepTier: tierFromRunLength(matchLen),
    finalClearedCells: [],
    finalCrateBreakCells: [],
    finalMatchedGroups: [],
    resolveFinalChainIndex: () => (tilesCleared > 0 ? 1 : 0)
  });
  const steelSummary = summarizeSteelStage(steelStage);
  objectiveTelemetry.obstacleClears += steelSummary.steelRemovedCount;
  soloStageTelemetry = mergeSoloStageTelemetry(soloStageTelemetry, steelSummary.soloStageTelemetry);
  const cascadeChainIndex = tilesCleared > 0 || steelSummary.groupsCleared > 0 ? 1 : 0;
  const cascades = runCascadeLoop(ctx, cascadeChainIndex);

  return {
    groupsCleared: (tilesCleared > 0 ? 1 : 0) + steelSummary.groupsCleared + cascades.groupsCleared,
    totalTilesCleared: tilesCleared + steelSummary.totalTilesCleared + cascades.totalTilesCleared,
    coinEligibleClears: clearResult.coinEligibleClears + steelSummary.coinEligibleClears + cascades.coinEligibleClears,
    chainDepth: cascadeChainIndex + cascades.chainDepth,
    scoreGained: scoreGained + steelSummary.scoreGained + cascades.scoreGained,
    earnedKey: earnedKey || steelSummary.earnedKey || cascades.earnedKey,
    crateBreakCount: clearResult.crateBreakCount + cascades.crateBreakCount,
    scoreEvents: [...scoreEvents, ...(steelSummary.scoreEvent != null ? [steelSummary.scoreEvent] : []), ...cascades.scoreEvents],
    visualSteps: [...visualSteps, ...cascades.visualSteps],
    objectiveTelemetry: mergeObjectiveTelemetry(objectiveTelemetry, cascades.objectiveTelemetry),
    soloStageTelemetry: mergeSoloStageTelemetry(soloStageTelemetry, cascades.soloStageTelemetry),
    haptics: {
      strongestBomb: strongerBomb(strongestBomb, cascades.haptics.strongestBomb),
      wipeActivated: wipeActivated || cascades.haptics.wipeActivated
    }
  };
}

function emptyResolveResult(): ResolveResult {
  return {
    groupsCleared: 0,
    totalTilesCleared: 0,
    coinEligibleClears: 0,
    chainDepth: 0,
    scoreGained: 0,
    earnedKey: false,
    crateBreakCount: 0,
    scoreEvents: [],
    visualSteps: [],
    objectiveTelemetry: emptyObjectiveTelemetry(),
    soloStageTelemetry: emptySoloStageTelemetry(),
    haptics: {
      strongestBomb: null,
      wipeActivated: false
    }
  };
}

function mergeResolveResults(base: ResolveResult, cascades: ResolveResult): ResolveResult {
  return {
    groupsCleared: base.groupsCleared + cascades.groupsCleared,
    totalTilesCleared: base.totalTilesCleared + cascades.totalTilesCleared,
    coinEligibleClears: base.coinEligibleClears + cascades.coinEligibleClears,
    chainDepth: base.chainDepth + cascades.chainDepth,
    scoreGained: base.scoreGained + cascades.scoreGained,
    earnedKey: base.earnedKey || cascades.earnedKey,
    crateBreakCount: base.crateBreakCount + cascades.crateBreakCount,
    scoreEvents: [...base.scoreEvents, ...cascades.scoreEvents],
    visualSteps: [...base.visualSteps, ...cascades.visualSteps],
    objectiveTelemetry: mergeObjectiveTelemetry(base.objectiveTelemetry, cascades.objectiveTelemetry),
    soloStageTelemetry: mergeSoloStageTelemetry(base.soloStageTelemetry, cascades.soloStageTelemetry),
    haptics: {
      strongestBomb: strongerBomb(base.haptics.strongestBomb, cascades.haptics.strongestBomb),
      wipeActivated: base.haptics.wipeActivated || cascades.haptics.wipeActivated
    }
  };
}

function applyPostPowerupCascade(
  ctx: ResolveContext,
  base: ResolveResult,
  startChainIndex: number
): ResolveResult {
  const steelStage = applyPostClearSettle(ctx, {
    visualSteps: base.visualSteps,
    steelChainIndex: startChainIndex,
    finalStepTier: 3,
    finalClearedCells: [],
    finalCrateBreakCells: [],
    finalMatchedGroups: [],
    resolveFinalChainIndex: () => startChainIndex
  });
  const steelSummary = summarizeSteelStage(steelStage);
  base.coinEligibleClears += steelSummary.coinEligibleClears;
  base.totalTilesCleared += steelSummary.totalTilesCleared;
  base.groupsCleared += steelSummary.groupsCleared;
  base.scoreGained += steelSummary.scoreGained;
  base.earnedKey = base.earnedKey || steelSummary.earnedKey;
  base.objectiveTelemetry.obstacleClears += steelSummary.steelRemovedCount;
  base.soloStageTelemetry = mergeSoloStageTelemetry(base.soloStageTelemetry, steelSummary.soloStageTelemetry);
  if (steelSummary.scoreEvent != null) {
    base.scoreEvents.push(steelSummary.scoreEvent);
  }
  if (steelSummary.groupsCleared > 0) {
    base.chainDepth = Math.max(base.chainDepth, 1);
  }
  const cascadeChainIndex = steelSummary.groupsCleared > 0
    ? Math.max(1, startChainIndex)
    : startChainIndex;
  const cascades = runCascadeLoop(ctx, cascadeChainIndex);
  return mergeResolveResults(base, cascades);
}

function lockByCell(board: BoardState, pos: Position): 0 | 1 {
  const tile = board.cells[pos.row]?.[pos.col]?.tile;
  if (tile == null) {
    return 0;
  }
  return tile.lockHp;
}

function setTileKeepLock(board: BoardState, pos: Position, tile: TileData): void {
  const cell = board.cells[pos.row]?.[pos.col];
  if (cell?.kind !== "TILE" || cell.tile == null) {
    return;
  }
  const lockHp = cell.tile.lockHp;
  cell.tile.color = tile.color;
  cell.tile.special = tile.special;
  cell.tile.lockHp = lockHp;
}

function isTileCell(board: BoardState, pos: Position): boolean {
  const cell = board.cells[pos.row]?.[pos.col];
  return cell?.kind === "TILE" && cell.tile != null;
}

function clearableByPowerup(board: BoardState, row: number, col: number): boolean {
  const cell = board.cells[row]?.[col];
  return cell != null && (cell.kind === "TILE" || cell.kind === "CRATE");
}

function powerupScoreEvent(
  _ctx: ResolveContext,
  result: ResolveResult,
  tilesClearedForScore: number,
  shape: "LINE" | "COMPOSITE",
  anchor: Position,
  scoredCellKeys: string[]
): void {
  if (tilesClearedForScore <= 0) {
    return;
  }
  const matchLen = Math.max(3, Math.min(10, tilesClearedForScore));
  const rawPoints = powerupSweepRawScore(tilesClearedForScore);
  result.groupsCleared += 1;
  result.scoreGained += rawPoints;
  result.scoreEvents.push({
    tilesCleared: tilesClearedForScore,
    matchLen,
    chainIndex: 0,
    points: rawPoints,
    shape,
    anchor,
    scoredCellKeys
  });
  if (tilesClearedForScore >= 4) {
    result.earnedKey = true;
  }
}

function mergeClearResultIntoResolve(
  out: ResolveResult,
  clearResult: ReturnType<typeof clearByCellKeys>
): number {
  const clearedCount = clearResult.tilesCleared + clearResult.crateBreakCount + clearResult.steelCleared;
  out.coinEligibleClears += clearResult.coinEligibleClears;
  out.crateBreakCount += clearResult.crateBreakCount;
  out.objectiveTelemetry.crateBreaks += clearResult.crateBreakCount;
  out.objectiveTelemetry.obstacleClears += clearResult.crateBreakCount + clearResult.steelCleared + clearResult.lockClearedCount;
  out.soloStageTelemetry = mergeSoloStageTelemetry(out.soloStageTelemetry, clearResult.soloStageTelemetry);
  return clearedCount;
}

function appendBombResolveStage(
  ctx: ResolveContext,
  out: ResolveResult,
  bombSeeds: BombSeed[],
  options?: { sweepImpactAudio?: CollapseAnimStep["sweepImpactAudio"] }
): void {
  if (bombSeeds.length === 0) {
    return;
  }
  const bombStage = buildBombWaveFromSeeds(ctx.board, bombSeeds);
  const bombOrigin = bombSeeds[0]?.center ?? { row: 5, col: 5 };
  const bombClearedCells = [...bombStage.clearCells].map(parsePosKey);
  const bombPreClearSnapshot = cloneBoard(ctx.board);
  const bombCleared = clearByCellKeys(ctx.board, bombStage.clearCells, false, ctx.level, ctx.rng);
  const bombTilesCleared = bombCleared.tilesCleared;
  const bombMatchLen = Math.max(3, Math.min(10, Math.max(1, bombTilesCleared)));
  out.haptics.strongestBomb = strongerBomb(
    out.haptics.strongestBomb,
    strongestBombFromCounts(bombStage.detonatedBombsByType)
  );
  out.objectiveTelemetry.bombTriggers += bombStage.triggeredBombCount;
  out.objectiveTelemetry.specialTriggers += bombStage.triggeredBombCount;
  mergeClearResultIntoResolve(out, bombCleared);
  const bombVisualSteps = makeBombWaveVisualSteps(
    bombPreClearSnapshot,
    ctx.board,
    bombStage.frames,
    bombStage.clearCells,
    bombOrigin,
    tierFromRunLength(bombMatchLen),
    bombCleared.crateBreakCells
  );
  const firstBombStep = bombVisualSteps[0];
  if (options?.sweepImpactAudio != null && firstBombStep != null) {
    bombVisualSteps[0] = {
      ...firstBombStep,
      sweepImpactAudio: options.sweepImpactAudio
    };
  }
  out.visualSteps.push(...bombVisualSteps);
  if (bombTilesCleared <= 0) {
    return;
  }
  const bombCenter = centerFromCells(bombClearedCells);
  const bombDoomBonusSec = bombDoomBonusForDetonationsByType(bombStage.detonatedBombsByType);
  const rawPoints = bombRawScore(bombTilesCleared, bombStage.triggeredBombCount);
  out.groupsCleared += 1;
  out.totalTilesCleared += bombTilesCleared;
  out.scoreGained += rawPoints;
  out.objectiveTelemetry.specialClearTiles += bombTilesCleared;
  out.scoreEvents.push({
    tilesCleared: bombTilesCleared,
    matchLen: bombMatchLen,
    chainIndex: 0,
    points: rawPoints,
    shape: "COMPOSITE",
    anchor: bombCenter,
    scoredCellKeys: bombCleared.clearedTileKeys,
    cashAmount: bombCashForDetonationsByType(bombStage.detonatedBombsByType),
    doomBonusSec: bombDoomBonusSec,
    grooveBonusFrac: bombGrooveBonusForTiles(bombTilesCleared)
  });
  if (bombTilesCleared >= 4) {
    out.earnedKey = true;
  }
}

function appendWipeResolveStage(
  ctx: ResolveContext,
  out: ResolveResult,
  wipe: WipeSwapPayload
): void {
  const wipeTargetGroups = resolveWipeTargetGroups(ctx.board, wipe, ctx.rng);
  const wipeMatchedCells = flattenWipeMatchedCells(wipeTargetGroups);
  const wipeClearSet = buildWipeClearSet(wipeTargetGroups, wipe.activators);
  const wipeClearedCells = [...wipeClearSet].map(parsePosKey);
  const wipeCleared = clearByCellKeys(ctx.board, wipeClearSet, true, ctx.level, ctx.rng);
  const wipeTilesCleared = wipeCleared.tilesCleared;
  const wipeTotalClearCount = mergeClearResultIntoResolve(out, wipeCleared);
  const wipeMatchLen = Math.max(3, Math.min(10, wipeTilesCleared));
  const wipeCenter = centerFromCells(wipeClearedCells);
  if (wipeTilesCleared > 0) {
    const wipeDoomBonusSec = wipeDoomBonusForMatchedTiles(wipeMatchedCells.length);
    const wipeGrooveBonusFrac = wipeGrooveBonusForMatchedTiles(wipeMatchedCells.length);
    out.groupsCleared += 1;
    out.totalTilesCleared += wipeTilesCleared;
    out.scoreGained += wipeRawScore(wipeTilesCleared);
    out.haptics.wipeActivated = true;
    out.objectiveTelemetry.wipeTriggers += 1;
    out.objectiveTelemetry.specialTriggers += 1;
    out.objectiveTelemetry.wipeClearTiles += wipeTilesCleared;
    out.objectiveTelemetry.specialClearTiles += wipeTilesCleared;
    out.scoreEvents.push({
      tilesCleared: wipeTilesCleared,
      matchLen: wipeMatchLen,
      chainIndex: 0,
      points: wipeRawScore(wipeTilesCleared),
      shape: "COMPOSITE",
      anchor: wipeCenter,
      scoredCellKeys: wipeCleared.clearedTileKeys,
      cashAmount: wipeTilesCleared * 5,
      doomBonusSec: wipeDoomBonusSec,
      grooveBonusFrac: wipeGrooveBonusFrac
    });
    if (wipeTilesCleared >= 4) {
      out.earnedKey = true;
    }
  }
  if (wipeTotalClearCount <= 0) {
    return;
  }
  pushVisualStep(out.visualSteps, ctx.board, {
    tier: tierFromRunLength(wipeMatchLen),
    motions: [],
    clearedCells: wipeClearedCells,
    crateBreakCells: wipeCleared.crateBreakCells,
    matchedGroups: wipeClearedCells.length > 0
      ? [
          {
            cells: wipeClearedCells.map((cell) => ({ row: cell.row, col: cell.col })),
            shape: "COMPOSITE",
            center: wipeCenter
          }
        ]
      : [],
    chainIndex: 0,
    specialFx: "WIPE_DISSOLVE",
    wipeColor: wipeTargetGroups[0]?.color,
    wipeMatchedCells,
    wipeTargetGroups: wipeTargetGroups.map((group) => ({
      color: group.color,
      cells: group.matchedCells.map((cell) => ({ row: cell.row, col: cell.col }))
    })),
    wipeActivatorCells: wipe.activators.map((cell) => ({ row: cell.row, col: cell.col }))
  });
}

function pushSingleCellPowerupStep(
  board: BoardState,
  visualSteps: CollapseAnimStep[],
  options: {
    cell: Position;
    crateBreakCells: Position[];
    specialFx: "POWERUP_ROW_SWEEP" | "POWERUP_COL_SWEEP" | "POWERUP_ZIGZAG";
    durationOverrideSec: number;
    powerupPath: Position[];
    shape: "LINE" | "COMPOSITE";
    clearedTileOverlays?: Array<{ row: number; col: number; tile: TileData }>;
    sweepImpactAudio?: CollapseAnimStep["sweepImpactAudio"];
  }
): void {
  visualSteps.push(stepWithSnapshot(board, {
    tier: 3,
    motions: [],
    clearedCells: [{ row: options.cell.row, col: options.cell.col }],
    crateBreakCells: options.crateBreakCells,
    matchedGroups: [
      {
        cells: [{ row: options.cell.row, col: options.cell.col }],
        shape: options.shape,
        center: { row: options.cell.row, col: options.cell.col }
      }
    ],
    chainIndex: 0,
    specialFx: options.specialFx,
    durationOverrideSec: options.durationOverrideSec,
    powerupPath: options.powerupPath,
    powerupTarget: { row: options.cell.row, col: options.cell.col },
    clearedTileOverlays: options.clearedTileOverlays ?? [],
    sweepImpactAudio: options.sweepImpactAudio
  }));
}

function resolveLinearPowerupSweep(
  ctx: ResolveContext,
  out: ResolveResult,
  path: Position[],
  specialFx: "POWERUP_ROW_SWEEP" | "POWERUP_COL_SWEEP"
): { totalSweepScoreTiles: number; scoredCellKeys: string[] } {
  let totalSweepScoreTiles = 0;
  let impactIndex = 0;
  const scoredCellKeys = new Set<string>();
  for (const cell of path) {
    if (!clearableByPowerup(ctx.board, cell.row, cell.col)) {
      continue;
    }
    const sweepImpactAudio: CollapseAnimStep["sweepImpactAudio"] = {
      sfx: specialFx === "POWERUP_ROW_SWEEP" ? "row_ripper" : "column_cutter",
      impactIndex
    };
    impactIndex += 1;
    const targetCell = ctx.board.cells[cell.row]?.[cell.col];
    if (targetCell?.kind === "TILE" && targetCell.tile != null && isBombSpecial(targetCell.tile.special)) {
      appendBombResolveStage(ctx, out, [
        {
          center: { row: cell.row, col: cell.col },
          special: targetCell.tile.special
        }
      ], { sweepImpactAudio });
      continue;
    }
    const clearedOverlay = overlayTileAt(ctx.board, cell.row, cell.col);
    const directHitWipe = targetCell?.kind === "TILE" && targetCell.tile?.special === "WIPE";
    const clearResult = clearByCellKeys(
      ctx.board,
      new Set<string>([posKey(cell.row, cell.col)]),
      false,
      ctx.level,
      ctx.rng
    );
    const clearedCount = mergeClearResultIntoResolve(out, clearResult);
    if (clearedCount <= 0) {
      continue;
    }
    totalSweepScoreTiles += clearedCount;
    for (const key of clearResult.clearedTileKeys) {
      scoredCellKeys.add(key);
    }
    out.totalTilesCleared += clearedCount;
    pushSingleCellPowerupStep(ctx.board, out.visualSteps, {
      cell,
      crateBreakCells: clearResult.crateBreakCells,
      specialFx,
      durationOverrideSec: 0.07,
      powerupPath: [{ row: cell.row, col: cell.col }],
      shape: "LINE",
      clearedTileOverlays: clearedOverlay == null ? [] : [clearedOverlay],
      sweepImpactAudio
    });
    if (directHitWipe) {
      break;
    }
  }
  return {
    totalSweepScoreTiles,
    scoredCellKeys: [...scoredCellKeys]
  };
}

export function resolvePowerupHint(
  ctx: ResolveContext,
  a: Position,
  b: Position
): ResolveResult {
  if (!isTileCell(ctx.board, a) || !isTileCell(ctx.board, b)) {
    return emptyResolveResult();
  }
  const cellA = ctx.board.cells[a.row][a.col];
  const cellB = ctx.board.cells[b.row][b.col];
  if (cellA.tile == null || cellB.tile == null) {
    return emptyResolveResult();
  }

  const tileA = cloneTileData(cellA.tile);
  const tileB = cloneTileData(cellB.tile);
  const lockA = lockByCell(ctx.board, a);
  const lockB = lockByCell(ctx.board, b);
  setTileKeepLock(ctx.board, a, tileB);
  setTileKeepLock(ctx.board, b, tileA);
  const afterA = ctx.board.cells[a.row][a.col];
  const afterB = ctx.board.cells[b.row][b.col];
  if (afterA.kind === "TILE" && afterA.tile != null) {
    afterA.tile.lockHp = lockA;
  }
  if (afterB.kind === "TILE" && afterB.tile != null) {
    afterB.tile.lockHp = lockB;
  }

  const base = emptyResolveResult();
  base.visualSteps.push(stepWithSnapshot(ctx.board, {
    tier: 3,
    motions: [],
    clearedCells: [
      { row: a.row, col: a.col },
      { row: b.row, col: b.col }
    ],
    matchedGroups: [
      {
        cells: [
          { row: a.row, col: a.col },
          { row: b.row, col: b.col }
        ],
        shape: "LINE",
        center: { row: Math.round((a.row + b.row) / 2), col: Math.round((a.col + b.col) / 2) }
      }
    ],
    chainIndex: 0,
    specialFx: "POWERUP_HINT",
    durationOverrideSec: 0.2,
    powerupPath: [
      { row: a.row, col: a.col },
      { row: b.row, col: b.col }
    ],
    powerupTarget: { row: b.row, col: b.col }
  }));

  if (!hasAnyMatch(ctx.board)) {
    return base;
  }
  const cascades = runCascadeLoop(ctx, 1);
  return mergeResolveResults(base, cascades);
}

export function resolvePowerupRowRip(
  ctx: ResolveContext,
  row: number
): ResolveResult {
  if (row < 0 || row >= ctx.board.size) {
    return emptyResolveResult();
  }

  const out = emptyResolveResult();
  const anchor = { row, col: Math.floor(ctx.board.size / 2) };
  const path = Array.from({ length: ctx.board.size }, (_unused, col) => ({ row, col }));
  const sweep = resolveLinearPowerupSweep(ctx, out, path, "POWERUP_ROW_SWEEP");

  powerupScoreEvent(ctx, out, sweep.totalSweepScoreTiles, "LINE", anchor, sweep.scoredCellKeys);
  out.chainDepth = out.groupsCleared > 0 ? 1 : 0;
  return applyPostPowerupCascade(ctx, out, out.groupsCleared > 0 ? 1 : 0);
}

export function resolvePowerupColCut(
  ctx: ResolveContext,
  col: number
): ResolveResult {
  if (col < 0 || col >= ctx.board.size) {
    return emptyResolveResult();
  }

  const out = emptyResolveResult();
  const anchor = { row: Math.floor(ctx.board.size / 2), col };
  const path = Array.from({ length: ctx.board.size }, (_unused, rowIndex) => ({
    row: ctx.board.size - 1 - rowIndex,
    col
  }));
  const sweep = resolveLinearPowerupSweep(ctx, out, path, "POWERUP_COL_SWEEP");

  powerupScoreEvent(ctx, out, sweep.totalSweepScoreTiles, "LINE", anchor, sweep.scoredCellKeys);
  out.chainDepth = out.groupsCleared > 0 ? 1 : 0;
  return applyPostPowerupCascade(ctx, out, out.groupsCleared > 0 ? 1 : 0);
}

const TRANSMUTE_SPECIALS: Array<"BOMB_3" | "BOMB_4" | "BOMB_5" | "WIPE"> = [
  "BOMB_3",
  "BOMB_4",
  "BOMB_5",
  "WIPE"
];

export function resolvePowerupTransmute(
  ctx: ResolveContext,
  target: Position
): ResolveResult {
  if (!isTileCell(ctx.board, target)) {
    return emptyResolveResult();
  }
  const tile = ctx.board.cells[target.row][target.col].tile;
  if (tile == null) {
    return emptyResolveResult();
  }
  tile.special = TRANSMUTE_SPECIALS[ctx.rng.nextInt(TRANSMUTE_SPECIALS.length)] ?? "BOMB_3";
  const out = emptyResolveResult();
  out.visualSteps.push(stepWithSnapshot(ctx.board, {
    tier: 3,
    motions: [],
    clearedCells: [{ row: target.row, col: target.col }],
    matchedGroups: [
      {
        cells: [{ row: target.row, col: target.col }],
        shape: "COMPOSITE",
        center: { row: target.row, col: target.col }
      }
    ],
    chainIndex: 0,
    specialFx: "POWERUP_TRANSMUTE",
    durationOverrideSec: 0.2,
    powerupTarget: { row: target.row, col: target.col }
  }));
  return out;
}

function buildZigzagPath(board: BoardState, origin: Position, length: number): Position[] {
  const path: Position[] = [];
  const visited = new Set<string>();
  let row = origin.row;
  let col = origin.col;
  let horizontal = origin.col <= Math.floor((board.size - 1) / 2) ? 1 : -1;
  let vertical = origin.row < Math.floor(board.size / 2) ? 1 : -1;
  const maxSteps = board.size * board.size * 2;

  for (let step = 0; step < maxSteps && path.length < length; step += 1) {
    if (inBounds(board, row, col)) {
      const key = posKey(row, col);
      if (!visited.has(key)) {
        visited.add(key);
        path.push({ row, col });
      }
    }

    col += horizontal;
    if (col < 0 || col >= board.size) {
      col = Math.max(0, Math.min(board.size - 1, col));
      horizontal *= -1;
      row += vertical;
      if (row < 0 || row >= board.size) {
        vertical *= -1;
        row = Math.max(0, Math.min(board.size - 1, row));
      }
      continue;
    }

    if (step % 2 === 1) {
      row += vertical;
      if (row < 0 || row >= board.size) {
        vertical *= -1;
        row = Math.max(0, Math.min(board.size - 1, row));
      }
    }
  }

  if (path.length >= length) {
    return path;
  }

  for (let r = 0; r < board.size && path.length < length; r += 1) {
    for (let c = 0; c < board.size && path.length < length; c += 1) {
      const key = posKey(r, c);
      if (visited.has(key)) {
        continue;
      }
      visited.add(key);
      path.push({ row: r, col: c });
    }
  }
  return path;
}

export function resolvePowerupZigzag(
  ctx: ResolveContext,
  origin: Position
): ResolveResult {
  if (!inBounds(ctx.board, origin.row, origin.col)) {
    return emptyResolveResult();
  }
  const out = emptyResolveResult();
  const path = buildZigzagPath(ctx.board, origin, 14);
  const visualPath = path.map((cell) => ({ row: cell.row, col: cell.col }));
  let totalSweepScoreTiles = 0;
  const scoredCellKeys = new Set<string>();

  for (const cell of path) {
    if (!clearableByPowerup(ctx.board, cell.row, cell.col)) {
      continue;
    }
    const targetCell = ctx.board.cells[cell.row]?.[cell.col];
    if (targetCell?.kind === "TILE" && targetCell.tile != null) {
      if (targetCell.tile.special === "WIPE") {
        appendWipeResolveStage(ctx, out, {
          activators: [{ row: cell.row, col: cell.col }],
          selection: {
            kind: "RANDOM_VISIBLE_COLORS",
            count: 1
          }
        });
        continue;
      }
      if (isBombSpecial(targetCell.tile.special)) {
        appendBombResolveStage(ctx, out, [
          {
            center: { row: cell.row, col: cell.col },
            special: targetCell.tile.special
          }
        ]);
        continue;
      }
    }
    const clearSet = new Set<string>([posKey(cell.row, cell.col)]);
    const clearResult = clearByCellKeys(ctx.board, clearSet, false, ctx.level, ctx.rng);
    const clearedCount = mergeClearResultIntoResolve(out, clearResult);
    if (clearedCount <= 0) {
      continue;
    }
    totalSweepScoreTiles += clearedCount;
    for (const key of clearResult.clearedTileKeys) {
      scoredCellKeys.add(key);
    }
    out.totalTilesCleared += clearedCount;
    pushSingleCellPowerupStep(ctx.board, out.visualSteps, {
      cell,
      crateBreakCells: clearResult.crateBreakCells,
      specialFx: "POWERUP_ZIGZAG",
      durationOverrideSec: 0.06,
      powerupPath: visualPath,
      shape: "COMPOSITE"
    });
  }

  powerupScoreEvent(ctx, out, totalSweepScoreTiles, "COMPOSITE", origin, [...scoredCellKeys]);
  out.chainDepth = out.groupsCleared > 0 ? 1 : 0;
  return applyPostPowerupCascade(ctx, out, out.groupsCleared > 0 ? 1 : 0);
}

